import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onActivated, onDeactivated, onMounted, ref, watch } from 'vue';

import { SnowplowContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useMetaStore, useRoutingStore } from '@/helpers/composables/useStores';

import { AppDirection } from '@/enums/app-direction';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';


export default /*#__PURE__*/_defineComponent({
  __name: 'HiddenHorizontalScrollbar',
  props: {
    resetScrollOnChange: { type: Boolean, default: false },
    allItemsLoaded: { type: Boolean, default: true },
    emitOnScroll: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    fadedEdge: { type: Boolean, default: false },
    hideNav: { type: Boolean, default: false },
    scrollSnap: { type: Boolean },
    fillWidth: { type: Boolean },
    additionalContexts: { default: () => [] }
  },
  emits: ["onScrollRight", "scrollbarClicked"],
  setup(__props: any, { expose, emit: emits }: { emit: ({
	(e: 'onScrollRight'): void;
	(e: 'scrollbarClicked', label: 'left' | 'right'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { resetScrollOnChange: boolean, allItemsLoaded: boolean, emitOnScroll: boolean, disabled: boolean, fadedEdge: boolean, hideNav: boolean, scrollSnap?: boolean, fillWidth?: boolean, additionalContexts: SnowplowContext[] };





const { activeRoute, lastRoute } = useRoutingStore();
const { appDirection } = useMetaStore();

const scroller = ref<Element | null>(null);
const isScrollBarVisible = ref(false);
const curScrollPos = ref(0);
const savedScrollPos = ref(0);
const isAtEnd = ref(false);
const selectedItem = ref(0);

const activeRouteName = computed(() => useRoutingStore().activeRoute.value);
watch(activeRouteName, () => {
	if (
		props.resetScrollOnChange &&
		activeRouteName.value?.meta?.tab === 'detail' &&
		lastRoute.value?.params.title !== activeRoute.value?.params.title
	) {
		resetScrollPosition();
	}
});

onMounted(() => {
	nextTick(() => {
		checkScrollable();
	});
});

onActivated(() => {
	if (Math.abs(savedScrollPos.value) > 0) {
		scroller.value?.scrollTo({ top: 0, left: savedScrollPos.value });
		selectedItem.value = 0;
	}
});

onDeactivated(() => {
	if (Math.abs(curScrollPos.value) > 0) {
		savedScrollPos.value = curScrollPos.value;
	}
});

const isAtStart = computed(() => !!(curScrollPos?.value === 0));

const isRTL = computed(() => appDirection.value === AppDirection.RTL);

function getScrollPosition(scrollLeftAmount: number): number {
	return isRTL.value ? -scrollLeftAmount : scrollLeftAmount;
}

function scrolled() {
	if (!scroller.value) return;

	curScrollPos.value = scroller.value.scrollLeft;

	// Calculate the scroll progress
	const progress = Math.round(
		(Math.abs(scroller.value.scrollLeft) / (scroller.value.scrollWidth - scroller.value.clientWidth)) * 100
	);
	if (
		progress < 95 ||
		(scroller.value.scrollWidth === 0 &&
			scroller.value.clientWidth === 0 &&
			scroller.value.clientHeight === 0 &&
			props.allItemsLoaded)
	) {
		isAtEnd.value = false;
	} else {
		isAtEnd.value = true;
	}

	if (progress > 40) {
		emits('onScrollRight');
	}
	if (isAtEnd.value && !props.allItemsLoaded) {
		emits('onScrollRight');
		isAtEnd.value = false;
	}
}

function scrollToEnd() {
	scrollBy(-(scroller.value?.clientWidth! - 90));
	trackScroll('left');
}

function scrollToStart() {
	scrollBy(scroller.value?.clientWidth! - 90);
	trackScroll('right');
}

function scrollBy(n: number | undefined) {
	scroller.value?.scrollBy({ top: 0, left: getScrollPosition(n!), behavior: 'smooth' });
}

function resetScrollPosition() {
	if (!scroller.value) return;
	scroller.value.scrollTo({ top: 0, left: 0, behavior: 'auto' });
	selectedItem.value = 0;
}

function trackScroll(label: 'left' | 'right') {
	if (props.emitOnScroll) {
		emits('scrollbarClicked', label);
	} else {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'horizontal_scroller_clicked',
				label,
			},
			props.additionalContexts
		);
	}
}

function handleMutations(mutationsList: MutationRecord[]) {
	for (const mutation of mutationsList) {
		if (mutation.type === 'childList') {
			// Check scrollbar visibility when mutations occur
			isScrollBarVisible.value = isHorizontalScrollBarVisible(scroller.value);
		}
	}
}

function isHorizontalScrollBarVisible(element: Element | null) {
	return element !== null && element?.scrollWidth > element?.clientWidth;
}

// this function checks if the scrollbar is visible
function checkScrollable() {
	isScrollBarVisible.value = isHorizontalScrollBarVisible(scroller.value);
	const observer = new MutationObserver(handleMutations);
	// Start observing the target element for mutations
	observer.observe(scroller.value as Node, { childList: true, subtree: true });
}

expose({
	scroller,
});

return { __sfc: true,props, emits, activeRoute, lastRoute, appDirection, scroller, isScrollBarVisible, curScrollPos, savedScrollPos, isAtEnd, selectedItem, activeRouteName, isAtStart, isRTL, getScrollPosition, scrolled, scrollToEnd, scrollToStart, scrollBy, resetScrollPosition, trackScroll, handleMutations, isHorizontalScrollBarVisible, checkScrollable, faChevronLeft, faChevronRight }
}

})